/**
 * SessionItem - A single session row in the sidebar list
 *
 * For active sessions: shows an Archive button on hover.
 * For archived sessions: shows Restore and Delete-permanently buttons.
 */

import React from 'react';
import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import {
  Archive as ArchiveIcon,
  Restore as RestoreIcon,
  DeleteForever as DeleteForeverIcon
} from '@mui/icons-material';

function formatRelativeDate(isoDate) {
  if (!isoDate) return '';
  try {
    const d = new Date(isoDate);
    const now = new Date();
    const diffMs = now - d;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
  } catch {
    return '';
  }
}

/**
 * SessionItem Component
 *
 * @param {Object} props
 * @param {Object} props.session - Session data { id, title, lastActivity, status }
 * @param {boolean} props.isActive - Whether this session is currently active
 * @param {Function} props.onSelect - Called when session is selected
 * @param {Function} props.onArchive - Called when archive button is clicked
 * @param {Function} [props.onRestore] - Called when restore button is clicked (archived only)
 * @param {Function} [props.onDelete] - Called when delete button is clicked (archived only)
 * @param {boolean} [props.isArchived=false] - Whether this session is archived
 */
const SessionItem = ({ session, isActive, onSelect, onArchive, onRestore, onDelete, isArchived = false }) => {
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect(session.id);
    }
  };

  const handleArchiveClick = (e) => {
    e.stopPropagation();
    onArchive(session.id);
  };

  const handleRestoreClick = (e) => {
    e.stopPropagation();
    onRestore?.(session.id);
  };

  const handleDeleteClick = (e) => {
    e.stopPropagation();
    if (window.confirm(`Permanently delete "${session.title || 'this conversation'}"? This cannot be undone.`)) {
      onDelete?.(session.id);
    }
  };

  return (
    <Box
      className={`session-item${isActive ? ' active' : ''}${isArchived ? ' archived' : ''}`}
      onClick={() => onSelect(session.id)}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      aria-label={`${isArchived ? 'Archived: ' : ''}${session.title}`}
      aria-pressed={isActive}
      sx={isArchived ? { opacity: 0.7 } : {}}
    >
      <Box className="session-item-content">
        <Typography className="session-item-title" title={session.title}>
          {session.title || 'Untitled conversation'}
        </Typography>
        <Typography className="session-item-date">
          {formatRelativeDate(session.lastActivity)}
        </Typography>
      </Box>

      {isArchived ? (
        <Box sx={{ display: 'flex', gap: 0.25, flexShrink: 0 }}>
          <Tooltip title="Restore conversation">
            <IconButton
              className="session-archive-btn"
              size="small"
              onClick={handleRestoreClick}
              aria-label="Restore this conversation"
              sx={{ mt: '-2px' }}
            >
              <RestoreIcon sx={{ fontSize: '0.875rem' }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete permanently">
            <IconButton
              className="session-archive-btn"
              size="small"
              onClick={handleDeleteClick}
              aria-label="Delete this conversation permanently"
              sx={{ mt: '-2px', color: 'error.main' }}
            >
              <DeleteForeverIcon sx={{ fontSize: '0.875rem' }} />
            </IconButton>
          </Tooltip>
        </Box>
      ) : (
        <Tooltip title="Archive conversation">
          <IconButton
            className="session-archive-btn"
            size="small"
            onClick={handleArchiveClick}
            aria-label="Archive this conversation"
            sx={{ mt: '-2px' }}
          >
            <ArchiveIcon sx={{ fontSize: '0.875rem' }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};

export default SessionItem;
